import React, {useState, useEffect, useCallback} from 'react';
import {Dropdown, DropdownOptionProps, TPAComponentsProvider} from 'wix-ui-tpa';
import {CurrencySymbols, GetCurrencyDisplayedValue} from '../../../settings/services/currency.utility';
import {classes as st} from './CurrenciesDropdown.st.css';
import {TranslationMap} from '../../../types/common';
import {useCountryInfoHook} from '../../hooks/useCountryInfoHook';
import {PopoverWrapper} from '../PopoverWrapper/PopoverWrapper';

export interface CurrenciesDropdownProps {
  currencies: string[];
  selectedCurrency: string;

  onChange: (code: string) => void;

  currencySymbols: CurrencySymbols;
  isMobile: boolean;
  isRtl: boolean;
  translations: TranslationMap;
  style?: {height?: number; width?: number};
  shouldShowSuggestCurrency: boolean;
  suggestedCurrencyCode: string;
}

export const CurrenciesDropdown = (props: CurrenciesDropdownProps) => {
  const {
    currencies,
    selectedCurrency,
    onChange,
    currencySymbols,
    isMobile,
    isRtl,
    translations,
    shouldShowSuggestCurrency,
    suggestedCurrencyCode,
  } = props;

  const [isOpen, setIsOpen] = useState(true);

  const {userCountryInfo} = useCountryInfoHook(suggestedCurrencyCode);

  const getTranslationByKey = useCallback(
    (key: string) => {
      /* istanbul ignore next: need test - empty value */
      return translations?.[key] ? translations[key] : '';
    },
    [translations]
  );

  useEffect(() => {
    // Set default currency if it's stored in local storage
    const storedSelectedCurrency = localStorage.getItem('selectedCurrency');
    if (storedSelectedCurrency && currencies?.includes(storedSelectedCurrency)) {
      onChange(storedSelectedCurrency);
    }
  }, [onChange, currencies]);

  /* istanbul ignore next: need test - currencies default value */
  const getCurrencyOptions = useCallback(
    (currenciesParam = [], currencySymbolsParam = {}) => {
      return currenciesParam.map((currency) => ({
        id: currency,
        value: GetCurrencyDisplayedValue(
          currency,
          currencySymbolsParam,
          getTranslationByKey('currencyConverter.dropdown.defaultCurrency.text')
        ),
        isSelectable: true,
      }));
    },
    [getTranslationByKey]
  );

  const [currencyOptions, setCurrencyOptions] = useState<DropdownOptionProps[]>(() =>
    getCurrencyOptions(currencies, currencySymbols)
  );

  useEffect(() => {
    /* istanbul ignore next: need test - currencies default */
    setCurrencyOptions(getCurrencyOptions(currencies, currencySymbols));
  }, [currencies, currencySymbols, getCurrencyOptions]);

  const getKeyForHackRenderOfDropdown = useCallback(() => {
    return GetCurrencyDisplayedValue(
      selectedCurrency,
      currencySymbols,
      getTranslationByKey('currencyConverter.dropdown.defaultCurrency.text')
    );
  }, [selectedCurrency, currencySymbols, getTranslationByKey]);

  const getDropdown = () => (
    <div
      data-hook="currency-converter-container"
      className={st.element}
      role="button"
      tabIndex={0}
      onClick={() => {
        setIsOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          setIsOpen(false);
        }
      }}
      aria-label={getTranslationByKey('sr.currencyConverter.widget')}>
      <Dropdown
        className={st.dropdownComponent}
        aria-label={getTranslationByKey('sr.currencyConverter.widget')}
        key={getKeyForHackRenderOfDropdown()}
        data-hook="currencies-dropdown"
        options={currencyOptions}
        initialSelectedId={selectedCurrency}
        onChange={(option: DropdownOptionProps) => {
          onChange(option.id);
          localStorage.setItem('selectedCurrency', option.id);
        }}
      />
    </div>
  );

  const shouldShowPopover =
    shouldShowSuggestCurrency &&
    isOpen &&
    suggestedCurrencyCode &&
    suggestedCurrencyCode !== selectedCurrency &&
    currencies?.includes(suggestedCurrencyCode);

  return (
    <TPAComponentsProvider value={{mobile: isMobile, rtl: isRtl}}>
      {shouldShowPopover ? (
        <PopoverWrapper
          isMobile={isMobile}
          setIsOpen={setIsOpen}
          onChange={onChange}
          selectedCurrency={selectedCurrency}
          userCountryInfo={userCountryInfo}
          getTranslationByKey={getTranslationByKey}>
          {getDropdown()}
        </PopoverWrapper>
      ) : (
        getDropdown()
      )}
    </TPAComponentsProvider>
  );
};
